/**
 * 确定性伪随机数生成器（mulberry32）
 *
 * 纪律：模拟核心内**禁止**使用 Math.random()。
 * 一切随机都来自 setup 中的 mapSeed / eventSeed 派生流，
 * 这是异步对战、回放与服务端复算校验的基础。
 */

export type Rng = () => number;

export function mulberry32(seed: number): Rng {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** 从主种子派生子随机流（不同用途互不干扰） */
export function deriveSeed(seed: number, label: string): number {
  let h = 2166136261 ^ seed;
  for (let i = 0; i < label.length; i++) {
    h ^= label.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** [min, max) 的整数 */
export function randInt(rng: Rng, min: number, max: number): number {
  return min + Math.floor(rng() * (max - min));
}

/** 从数组中等概率取一个元素 */
export function pick<T>(rng: Rng, arr: readonly T[]): T {
  if (arr.length === 0) throw new Error("pick(): empty array");
  return arr[Math.floor(rng() * arr.length)] as T;
}

/** 洗牌（Fisher–Yates，返回新数组） */
export function shuffle<T>(rng: Rng, arr: readonly T[]): T[] {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [out[i], out[j]] = [out[j] as T, out[i] as T];
  }
  return out;
}

/**
 * FNV-1a 32 位哈希：用于生成 SimResult.checksum。
 * 服务端用同一函数对复算结果做比对，防作弊的第一道闸门。
 */
export function fnv1a(input: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, "0");
}
